// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import React, { useState, useEffect } from 'react';
import { Modal, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import { useAppDispatch } from '../../../../store';
import { updateCandidateProfile, Candidate } from '../../../../store/interviewHostSlice';
import styles from './candidates.module.scss';

interface EditCandidateModalProps {
  open: boolean;
  onClose: () => void;
  candidate: Candidate;
  jobId: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  current_title?: string;
  location?: string;
  experience_years?: string;
}

const EditCandidateModal: React.FC<EditCandidateModalProps> = ({ open, onClose, candidate, jobId }) => {
  const dispatch = useAppDispatch();
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});

  // Form state
  const [formData, setFormData] = useState({
    name: candidate.name,
    email: candidate.email,
    phone: candidate.phone,
    current_title: candidate.current_title,
    location: candidate.location,
    experience_years: candidate.experience_years
  });

  // Reset form when candidate changes or modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        name: candidate.name,
        email: candidate.email,
        phone: candidate.phone,
        current_title: candidate.current_title,
        location: candidate.location,
        experience_years: candidate.experience_years
      });
      setError(null);
      setErrors({});
    }
  }, [open, candidate]);

  const validateField = (field: keyof FormErrors, value: string | number): string | undefined => {
    switch (field) {
      case 'name':
        if (!String(value).trim()) return 'Name is required';
        break;
      case 'email':
        if (!String(value).trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value))) return 'Invalid email format';
        break;
      case 'phone':
        if (!String(value).trim()) return 'Phone is required';
        break;
      case 'current_title':
        if (!String(value).trim()) return 'Current title is required';
        break;
      case 'location':
        if (!String(value).trim()) return 'Location is required';
        break;
      case 'experience_years':
        if (Number(value) < 0 || Number(value) > 60) return 'Must be between 0 and 60';
        break;
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Check if at least one field has changed
    const hasChanges =
      formData.name !== candidate.name ||
      formData.email !== candidate.email ||
      formData.phone !== candidate.phone ||
      formData.current_title !== candidate.current_title ||
      formData.location !== candidate.location ||
      formData.experience_years !== candidate.experience_years;

    if (!hasChanges) {
      setError('No changes detected. Please modify at least one field.');
      return false;
    }

    // Validate all fields
    Object.keys(formData).forEach((key) => {
      const fieldError = validateField(key as keyof FormErrors, formData[key as keyof typeof formData]);
      if (fieldError) {
        newErrors[key as keyof FormErrors] = fieldError;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = async (): Promise<void> => {
    if (!validateForm()) {
      return;
    }

    setUpdating(true);
    setError(null);

    try {
      // Only send changed fields
      const updates: any = {};
      if (formData.name !== candidate.name) updates.name = formData.name.trim();
      if (formData.email !== candidate.email) updates.email = formData.email.trim();
      if (formData.phone !== candidate.phone) updates.phone = formData.phone.trim();
      if (formData.current_title !== candidate.current_title) updates.current_title = formData.current_title.trim();
      if (formData.location !== candidate.location) updates.location = formData.location.trim();
      if (formData.experience_years !== candidate.experience_years)
        updates.experience_years = formData.experience_years;

      await dispatch(
        updateCandidateProfile({
          candidateId: candidate.candidate_id,
          jobId,
          updates
        })
      ).unwrap();

      onClose();
    } catch (err: any) {
      console.error('Failed to update candidate:', err);
      setError(err || 'Failed to update candidate profile. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleClose = (): void => {
    if (!updating) {
      onClose();
    }
  };

  const handleChange = (field: keyof typeof formData, value: string | number): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
    // Clear field error on change
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleBlur = (field: keyof FormErrors): void => {
    const fieldError = validateField(field, formData[field as keyof typeof formData]);
    if (fieldError) {
      setErrors((prev) => ({ ...prev, [field]: fieldError }));
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Edit Candidate</h2>
          <button className={styles.modalClose} onClick={handleClose} disabled={updating}>
            <CloseIcon />
          </button>
        </div>

        <div className={styles.modalBody}>
          {error && (
            <div className={styles.errorMessage}>
              <ErrorIcon className={styles.errorIcon} />
              <p>{error}</p>
            </div>
          )}

          <label className={styles.formLabel}>
            Name
            <input
              type="text"
              className={`${styles.formInput} ${errors.name ? styles.inputError : ''}`}
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              onBlur={() => handleBlur('name')}
              disabled={updating}
              placeholder="Full name"
            />
            {errors.name && <span className={styles.errorText}>{errors.name}</span>}
          </label>

          <label className={styles.formLabel}>
            Email
            <input
              type="email"
              className={`${styles.formInput} ${errors.email ? styles.inputError : ''}`}
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              onBlur={() => handleBlur('email')}
              disabled={updating}
              placeholder="email@example.com"
            />
            {errors.email && <span className={styles.errorText}>{errors.email}</span>}
          </label>

          <label className={styles.formLabel}>
            Phone
            <input
              type="text"
              className={`${styles.formInput} ${errors.phone ? styles.inputError : ''}`}
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              onBlur={() => handleBlur('phone')}
              disabled={updating}
              placeholder="+91 9999999999"
            />
            {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
          </label>

          <label className={styles.formLabel}>
            Current Title
            <input
              type="text"
              className={`${styles.formInput} ${errors.current_title ? styles.inputError : ''}`}
              value={formData.current_title}
              onChange={(e) => handleChange('current_title', e.target.value)}
              onBlur={() => handleBlur('current_title')}
              disabled={updating}
              placeholder="Software Engineer"
            />
            {errors.current_title && <span className={styles.errorText}>{errors.current_title}</span>}
          </label>

          <label className={styles.formLabel}>
            Location
            <input
              type="text"
              className={`${styles.formInput} ${errors.location ? styles.inputError : ''}`}
              value={formData.location}
              onChange={(e) => handleChange('location', e.target.value)}
              onBlur={() => handleBlur('location')}
              disabled={updating}
              placeholder="Bangalore, India"
            />
            {errors.location && <span className={styles.errorText}>{errors.location}</span>}
          </label>

          <label className={styles.formLabel}>
            Experience (Years)
            <input
              type="number"
              className={`${styles.formInput} ${errors.experience_years ? styles.inputError : ''}`}
              value={formData.experience_years}
              onChange={(e) => handleChange('experience_years', parseInt(e.target.value) || 0)}
              onBlur={() => handleBlur('experience_years')}
              disabled={updating}
              min="0"
              max="60"
              placeholder="5"
            />
            {errors.experience_years && <span className={styles.errorText}>{errors.experience_years}</span>}
          </label>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.btnSecondary} onClick={handleClose} disabled={updating}>
            Cancel
          </button>
          <button className={styles.btnPrimary} onClick={handleUpdate} disabled={updating}>
            {updating ? 'Updating...' : 'Update Candidate'}
          </button>
        </div>
      </Box>
    </Modal>
  );
};

export default EditCandidateModal;
